import { apiVersions, getLogicEndpoint } from '@/services/endpoints';
import { isLocalMode } from '@/services/localRuntimeClient';
import { getArmBaseUrl } from '@/services/armConfig';
import { ApiError } from '@/services/apiError';
import type {
  AutomationApp,
  StartStopResponse,
  ScaleConfiguration,
  RevisionsResponse,
  ImageTagsResponse,
  SetImageRequest,
  SetImageResponse,
} from '@/types/automation';

// =============================================================================
// HELPERS
// =============================================================================

/** In local mode, route to mock control plane; otherwise use the real logic endpoint. */
const getBaseUrl = (): string => (isLocalMode() ? getArmBaseUrl() : getLogicEndpoint());

/** Build the common application base URL segment. */
const buildAppUrl = (projectName: string, appName: string, suffix?: string): string => {
  const base = `${getBaseUrl()}/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}`;
  const path = suffix ? `${base}/${suffix}` : base;
  return `${path}?api-version=${apiVersions.otto}`;
};

/** Build standard headers with optional auth. */
const buildHeaders = (token: string): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!isLocalMode()) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Fetches an application including its live runningStatus.
 */
export const getApplication = async (
  projectName: string,
  appName: string,
  token: string,
): Promise<AutomationApp> => {
  const response = await fetch(buildAppUrl(projectName, appName), {
    method: 'GET',
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  return response.json();
};

/**
 * Starts an application. Returns 202 when starting, 200 when already running.
 */
export const startApplication = async (
  projectName: string,
  appName: string,
  token: string,
): Promise<StartStopResponse> => {
  const response = await fetch(buildAppUrl(projectName, appName, 'start'), {
    method: 'POST',
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  return response.json();
};

/**
 * Stops an application. Returns 202 when stopping, 200 when already stopped.
 */
export const stopApplication = async (
  projectName: string,
  appName: string,
  token: string,
): Promise<StartStopResponse> => {
  const response = await fetch(buildAppUrl(projectName, appName, 'stop'), {
    method: 'POST',
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  return response.json();
};

/**
 * Fetches the scale configuration for an application.
 */
export const getScaleConfiguration = async (
  projectName: string,
  appName: string,
  token: string,
): Promise<ScaleConfiguration> => {
  const response = await fetch(buildAppUrl(projectName, appName, 'scale'), {
    method: 'GET',
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  return response.json();
};

/**
 * Updates the scale configuration for an application via PUT.
 *
 * The backend performs a partial, scale-scoped merge: the supplied fields
 * (`minReplicas`, `maxReplicas`, `cooldownPeriod`, `pollingInterval`) are
 * applied while the system-managed autoscale `rules` are preserved untouched.
 * The response echoes the post-update scale state (without `rules`).
 */
export const updateScaleConfiguration = async (
  projectName: string,
  appName: string,
  token: string,
  config: ScaleConfiguration,
): Promise<ScaleConfiguration> => {
  const response = await fetch(buildAppUrl(projectName, appName, 'scale'), {
    method: 'PUT',
    headers: buildHeaders(token),
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  return response.json();
};

/**
 * Lists all revisions for an application.
 */
export const listRevisions = async (
  projectName: string,
  appName: string,
  token: string,
): Promise<RevisionsResponse> => {
  const response = await fetch(buildAppUrl(projectName, appName, 'revisions'), {
    method: 'GET',
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  return response.json();
};

/**
 * Fetches available image tags for the application's container image.
 */
export const getImageTags = async (
  projectName: string,
  appName: string,
  token: string,
): Promise<ImageTagsResponse> => {
  const response = await fetch(buildAppUrl(projectName, appName, 'imageTags'), {
    method: 'GET',
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  return response.json();
};

/**
 * Sets the container image for an application (changes runtime version).
 * Returns 200 (no-op if same image) or 202 (change in progress).
 */
export const setImage = async (
  projectName: string,
  appName: string,
  token: string,
  request: SetImageRequest,
): Promise<SetImageResponse> => {
  const response = await fetch(buildAppUrl(projectName, appName, 'setImage'), {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  return response.json();
};
